import {
  TimelineItem,
  TimelineDate,
  TimelineTask,
  WaitingItem,
  CreateTimelineDateDto,
  UpdateTimelineDateDto,
  CreateTimelineTaskDto,
  UpdateTimelineTaskDto,
  CreateWaitingItemDto,
  UpdateWaitingItemDto,
  DeadlineInfo,
} from "../types/timeline.types";
import { TijdlijnDatumType, TijdlijnTaakType } from "../enums";

const API_BASE_URL = process.env.REACT_APP_API_URL as string;

// Helper function to calculate deadline status
export const calculateDeadlineStatus = (
  deadline?: string,
  completedAt?: string,
): DeadlineInfo => {
  if (completedAt) {
    return {
      status: "completed",
      daysUntilDeadline: 0,
      isOverdue: false,
      displayText: "Afgerond",
      colorClass: "text-gray-500",
    };
  }

  if (!deadline) {
    return {
      status: "on-track",
      daysUntilDeadline: 0,
      isOverdue: false,
      displayText: "Geen deadline",
      colorClass: "text-gray-400",
    };
  }

  const now = new Date();
  const deadlineDate = new Date(deadline);
  const diffTime = deadlineDate.getTime() - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays < 0) {
    return {
      status: "overdue",
      daysUntilDeadline: diffDays,
      isOverdue: true,
      displayText: `${Math.abs(diffDays)} dagen overschreden`,
      colorClass: "text-red-500",
    };
  } else if (diffDays <= 3) {
    return {
      status: "due-soon",
      daysUntilDeadline: diffDays,
      isOverdue: false,
      displayText: diffDays === 0 ? "Vandaag" : `Over ${diffDays} dagen`,
      colorClass: "text-orange-500",
    };
  } else {
    return {
      status: "on-track",
      daysUntilDeadline: diffDays,
      isOverdue: false,
      displayText: `Over ${diffDays} dagen`,
      colorClass: "text-green-500",
    };
  }
};

// Helper function to format employee name
export const formatEmployeeName = (employee: {
  voornaam?: string | null;
  achternaam: string;
}): string => {
  if (employee.voornaam) {
    return `${employee.voornaam} ${employee.achternaam}`;
  }
  return employee.achternaam;
};

// Helper function to format date for display
export const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString("nl-NL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

// Helper function to format date for input
export const formatDateForInput = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toISOString().split("T")[0];
};

// Canonical set of key dossier dates (5 lifecycle dates)
export const DATE_TYPES: { type: TijdlijnDatumType; label: string }[] = [
  {
    type: TijdlijnDatumType.AANVRAAG_ONTVANGEN,
    label: "Aanvraag ontvangen",
  },
  {
    type: TijdlijnDatumType.ONDERZOEK,
    label: "Onderzoek",
  },
  {
    type: TijdlijnDatumType.VERSTUURD_AAN_BETROKKENE,
    label: "Verstuurd aan betrokkene",
  },
  {
    type: TijdlijnDatumType.VERSTUURD_AAN_OG,
    label: "Verstuurd aan OG",
  },
  {
    type: TijdlijnDatumType.VERSTUURD_DEFINITIEF_RAPPORT,
    label: "Verstuurd Definitief Rapport",
  },
];

// Shared helper to upsert the 5 key dates for a dossier
export const upsertTimelineDates = async (
  dossierKenmerk: string,
  existingDates: TimelineDate[],
  formData: Record<TijdlijnDatumType, string>,
): Promise<TimelineDate[]> => {
  const promises: Promise<any>[] = [];
  // existingDates kan verouderd zijn: een afgevinkte verstuur-taak maakt de
  // datum op de server zelf aan. Dan bijwerken i.p.v. dubbel aanmaken (400).
  const actueel = await TimelineService.getTimelineDates(dossierKenmerk);

  for (const { type } of DATE_TYPES) {
    const existingDate = existingDates.find((d) => d.datumType === type);
    const newDateValue = formData[type];

    if (existingDate) {
      const currentValue = formatDateForInput(existingDate.startDatum);
      if (!newDateValue) {
        // Leeggemaakt: verwijderen (backend legt een teamnotitie vast)
        promises.push(
          TimelineService.deleteTimelineDate(dossierKenmerk, existingDate.id),
        );
      } else if (newDateValue !== currentValue) {
        promises.push(
          TimelineService.updateTimelineDate(dossierKenmerk, existingDate.id, {
            startDatum: newDateValue,
          }),
        );
      }
    } else if (newDateValue) {
      const alAangemaakt = actueel.find((d) => d.datumType === type);
      promises.push(
        alAangemaakt
          ? TimelineService.updateTimelineDate(
              dossierKenmerk,
              alAangemaakt.id,
              {
                startDatum: newDateValue,
              },
            )
          : TimelineService.createTimelineDate(dossierKenmerk, {
              datumType: type,
              startDatum: newDateValue,
            }),
      );
    }
  }

  if (promises.length > 0) {
    await Promise.all(promises);
  }

  // Return the refreshed list from the API so callers can update local state
  return TimelineService.getTimelineDates(dossierKenmerk);
};

// API Service class
export class TimelineService {
  private static getAuthHeaders() {
    return {
      "Content-Type": "application/json",
      // Add any auth headers if needed
    };
  }

  // ===== ALL TIMELINE ITEMS =====
  static async getAllTimelineItems(
    dossierKenmerk: string,
  ): Promise<TimelineItem[]> {
    const response = await fetch(
      `${API_BASE_URL}/dossiers/${dossierKenmerk}/timeline`,
      {
        headers: this.getAuthHeaders(),
        credentials: "include",
      },
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch timeline items: ${response.statusText}`);
    }

    return response.json();
  }

  // ===== TIMELINE DATES =====
  static async getTimelineDates(
    dossierKenmerk: string,
  ): Promise<TimelineDate[]> {
    const response = await fetch(
      `${API_BASE_URL}/dossiers/${dossierKenmerk}/timeline/dates`,
      {
        headers: this.getAuthHeaders(),
        credentials: "include",
      },
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch timeline dates: ${response.statusText}`);
    }

    return response.json();
  }

  static async createTimelineDate(
    dossierKenmerk: string,
    data: CreateTimelineDateDto,
  ): Promise<TimelineDate> {
    const response = await fetch(
      `${API_BASE_URL}/dossiers/${dossierKenmerk}/timeline/dates`,
      {
        method: "POST",
        headers: this.getAuthHeaders(),
        credentials: "include",
        body: JSON.stringify(data),
      },
    );

    if (!response.ok) {
      throw new Error(`Failed to create timeline date: ${response.statusText}`);
    }

    return response.json();
  }

  static async updateTimelineDate(
    dossierKenmerk: string,
    dateId: string,
    data: UpdateTimelineDateDto,
  ): Promise<TimelineDate> {
    const response = await fetch(
      `${API_BASE_URL}/dossiers/${dossierKenmerk}/timeline/dates/${dateId}`,
      {
        method: "PATCH",
        headers: this.getAuthHeaders(),
        credentials: "include",
        body: JSON.stringify(data),
      },
    );

    if (!response.ok) {
      throw new Error(`Failed to update timeline date: ${response.statusText}`);
    }

    return response.json();
  }

  static async deleteTimelineDate(
    dossierKenmerk: string,
    dateId: string,
  ): Promise<void> {
    const response = await fetch(
      `${API_BASE_URL}/dossiers/${dossierKenmerk}/timeline/dates/${dateId}`,
      {
        method: "DELETE",
        headers: this.getAuthHeaders(),
        credentials: "include",
      },
    );

    if (!response.ok) {
      throw new Error(`Failed to delete timeline date: ${response.statusText}`);
    }
  }

  // ===== TIMELINE TASKS =====
  static async getTimelineTasks(
    dossierKenmerk: string,
  ): Promise<TimelineTask[]> {
    const response = await fetch(
      `${API_BASE_URL}/dossiers/${dossierKenmerk}/timeline/tasks`,
      {
        headers: this.getAuthHeaders(),
        credentials: "include",
      },
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch timeline tasks: ${response.statusText}`);
    }

    return response.json();
  }

  /** Voorstel-deadline op basis van de standaardwaarden (Beheer). */
  static async previewTaskDeadline(
    dossierKenmerk: string,
    taakType: TijdlijnTaakType,
    startDatum?: string,
  ): Promise<{ vervalDatum: string }> {
    const params = new URLSearchParams({ taakType });
    if (startDatum) params.set("startDatum", startDatum);
    const response = await fetch(
      `${API_BASE_URL}/dossiers/${dossierKenmerk}/timeline/tasks/deadline-preview?${params.toString()}`,
      {
        headers: this.getAuthHeaders(),
        credentials: "include",
      },
    );

    if (!response.ok) {
      throw new Error(
        `Failed to fetch deadline preview: ${response.statusText}`,
      );
    }

    return response.json();
  }

  static async createTimelineTask(
    dossierKenmerk: string,
    data: CreateTimelineTaskDto,
  ): Promise<TimelineTask> {
    const response = await fetch(
      `${API_BASE_URL}/dossiers/${dossierKenmerk}/timeline/tasks`,
      {
        method: "POST",
        headers: this.getAuthHeaders(),
        credentials: "include",
        body: JSON.stringify(data),
      },
    );

    if (!response.ok) {
      // D11: de servermelding doorgeven (bijv. een product dat niet kan).
      const fout = await response.json().catch(() => null);
      throw new Error(
        typeof fout?.message === "string"
          ? fout.message
          : `Failed to create timeline task: ${response.statusText}`,
      );
    }

    return response.json();
  }

  static async updateTimelineTask(
    dossierKenmerk: string,
    taskId: string,
    data: UpdateTimelineTaskDto,
  ): Promise<TimelineTask> {
    const response = await fetch(
      `${API_BASE_URL}/dossiers/${dossierKenmerk}/timeline/tasks/${taskId}`,
      {
        method: "PATCH",
        headers: this.getAuthHeaders(),
        credentials: "include",
        body: JSON.stringify(data),
      },
    );

    if (!response.ok) {
      throw new Error(`Failed to update timeline task: ${response.statusText}`);
    }

    return response.json();
  }

  static async deleteTimelineTask(
    dossierKenmerk: string,
    taskId: string,
  ): Promise<void> {
    const response = await fetch(
      `${API_BASE_URL}/dossiers/${dossierKenmerk}/timeline/tasks/${taskId}`,
      {
        method: "DELETE",
        headers: this.getAuthHeaders(),
        credentials: "include",
      },
    );

    if (!response.ok) {
      throw new Error(`Failed to delete timeline task: ${response.statusText}`);
    }
  }

  // ===== WAITING ITEMS =====
  static async getWaitingItems(dossierKenmerk: string): Promise<WaitingItem[]> {
    const response = await fetch(
      `${API_BASE_URL}/dossiers/${dossierKenmerk}/timeline/waiting`,
      {
        headers: this.getAuthHeaders(),
        credentials: "include",
      },
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch waiting items: ${response.statusText}`);
    }

    return response.json();
  }

  static async createWaitingItem(
    dossierKenmerk: string,
    data: CreateWaitingItemDto,
  ): Promise<WaitingItem> {
    const response = await fetch(
      `${API_BASE_URL}/dossiers/${dossierKenmerk}/timeline/waiting`,
      {
        method: "POST",
        headers: this.getAuthHeaders(),
        credentials: "include",
        body: JSON.stringify(data),
      },
    );

    if (!response.ok) {
      throw new Error(`Failed to create waiting item: ${response.statusText}`);
    }

    return response.json();
  }

  static async updateWaitingItem(
    dossierKenmerk: string,
    itemId: string,
    data: UpdateWaitingItemDto,
  ): Promise<WaitingItem> {
    const response = await fetch(
      `${API_BASE_URL}/dossiers/${dossierKenmerk}/timeline/waiting/${itemId}`,
      {
        method: "PATCH",
        headers: this.getAuthHeaders(),
        credentials: "include",
        body: JSON.stringify(data),
      },
    );

    if (!response.ok) {
      throw new Error(`Failed to update waiting item: ${response.statusText}`);
    }

    return response.json();
  }

  static async deleteWaitingItem(
    dossierKenmerk: string,
    itemId: string,
  ): Promise<void> {
    const response = await fetch(
      `${API_BASE_URL}/dossiers/${dossierKenmerk}/timeline/waiting/${itemId}`,
      {
        method: "DELETE",
        headers: this.getAuthHeaders(),
        credentials: "include",
      },
    );

    if (!response.ok) {
      throw new Error(`Failed to delete waiting item: ${response.statusText}`);
    }
  }

  // ===== SPECIAL ACTIONS =====
  static async sendReminder(
    dossierKenmerk: string,
    itemId: string,
  ): Promise<WaitingItem> {
    const response = await fetch(
      `${API_BASE_URL}/dossiers/${dossierKenmerk}/timeline/waiting/${itemId}/send-reminder`,
      {
        method: "PATCH",
        headers: this.getAuthHeaders(),
        credentials: "include",
      },
    );

    if (!response.ok) {
      throw new Error(`Failed to send reminder: ${response.statusText}`);
    }

    return response.json();
  }

  static async snoozeDeadline(
    dossierKenmerk: string,
    itemId: string,
  ): Promise<WaitingItem> {
    const response = await fetch(
      `${API_BASE_URL}/dossiers/${dossierKenmerk}/timeline/waiting/${itemId}/snooze`,
      {
        method: "PATCH",
        headers: this.getAuthHeaders(),
        credentials: "include",
      },
    );

    if (!response.ok) {
      throw new Error(`Failed to snooze deadline: ${response.statusText}`);
    }

    return response.json();
  }

  static async markWaitingItemDone(
    dossierKenmerk: string,
    itemId: string,
    done: boolean,
  ): Promise<WaitingItem> {
    const updateData: any = {};
    if (done) {
      updateData.afgerondOp = new Date().toISOString();
    } else {
      // To "undo" completion, we need to explicitly clear the field
      updateData.afgerondOp = null;
    }
    return this.updateWaitingItem(dossierKenmerk, itemId, updateData);
  }
}
